import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CrownGlyph } from '@/components/nav/icons';
import { cn } from '@/lib/utils';
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAdminRole } from '@/hooks/useAdminRole';
import { useAssessments } from '@/hooks/useAssessments';
import { useSubscription } from '@/hooks/useSubscription';
import { useAstrologyAccess } from '@/features/astrology/hooks/useAstrologyAccess';
import { PsyforgeMark } from '@/components/brand/PsyforgeMark';
import { useFeatureLastVisited } from '@/hooks/useFeatureLastVisited';
import { useUnreadConversationCount } from '@/hooks/useUnreadConversationCount';
import { NAVIGATION_GROUPS, navigationItemIsActive, visibleNavigationItems, type NavigationItem } from '@/config/navigation';

const HINT_ROUTES = new Set(['/reflect', '/mentor', '/career', '/relationships', '/liker', '/quizzes', '/integral']);

function NewFeatureDot({ route }: { route: string }) {
  const status = useFeatureLastVisited(route);
  if (!HINT_ROUTES.has(route) || status.kind !== 'never') return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span aria-label="New to you" className="relative ml-auto flex h-2 w-2 shrink-0 rounded-full bg-primary" />
      </TooltipTrigger>
      <TooltipContent side="right">New to you — try it out</TooltipContent>
    </Tooltip>
  );
}

export function AppSidebar({ collapsible = 'icon' }: { collapsible?: 'icon' | 'offcanvas' | 'none' }) {
  const { state } = useSidebar();
  const location = useLocation();
  const { isAdmin } = useAdminRole();
  const { hasTPSAssessment } = useAssessments();
  const { plan } = useSubscription();
  const { enabled: astrologyEnabled } = useAstrologyAccess();
  const unreadMail = useUnreadConversationCount();
  const collapsed = state === 'collapsed';
  const items = visibleNavigationItems({ isAdmin, hasTPSAssessment, astrologyEnabled });

  const renderItem = (item: NavigationItem) => {
    const active = navigationItemIsActive(item, location.pathname);
    const className = cn(
      'relative flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 group',
      active ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
    );
    const content = (
      <>
        {active && <span aria-hidden className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary" />}
        <item.icon className={cn('relative z-10 h-5 w-5 shrink-0 transition-transform', active && 'scale-105')} />
        {!collapsed && <span className="relative z-10">{item.label}</span>}
        {item.id === 'social' && unreadMail > 0 && (
          <span className="relative z-10 ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-primary-foreground">{unreadMail > 99 ? '99+' : unreadMail}</span>
        )}
        {!collapsed && <NewFeatureDot route={item.path} />}
      </>
    );
    const link = item.external ? (
      <a href={item.path} target="_blank" rel="noopener noreferrer" aria-label={item.label} className={className}>{content}</a>
    ) : (
      <Link to={item.path} aria-label={item.label} aria-current={active ? 'page' : undefined} className={className}>{content}</Link>
    );
    return (
      <SidebarMenuItem key={item.id}>
        <SidebarMenuButton asChild>
          {collapsed ? <Tooltip><TooltipTrigger asChild>{link}</TooltipTrigger><TooltipContent side="right">{item.label}</TooltipContent></Tooltip> : link}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible={collapsible} className="border-r border-border/50 transition-[width] duration-200 ease-out" aria-label="Main navigation">
      <div className={cn('h-16 flex items-center border-b border-border/50 px-4', collapsed ? 'justify-center' : 'justify-start gap-2')}>
        <PsyforgeMark size="sm" />
        {!collapsed && <span className="font-display font-bold text-lg">Psyforge</span>}
      </div>
      <SidebarContent className="px-3 py-4 pb-20">
        {NAVIGATION_GROUPS.map((group) => {
          const grouped = items.filter((item) => item.group === group);
          if (grouped.length === 0) return null;
          return (
            <SidebarGroup key={group}>
              <SidebarGroupLabel className={collapsed ? 'sr-only' : ''}>{group}</SidebarGroupLabel>
              <SidebarGroupContent><SidebarMenu>{grouped.map(renderItem)}</SidebarMenu></SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>
      <div className={cn('border-t border-border/50 mt-2 px-2 py-2', collapsed && 'flex justify-center')}>
        <Link to="/subscription" aria-label={`${plan} plan`} className={cn('flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60', plan === 'pro' ? 'text-primary' : 'text-muted-foreground')}>
          <CrownGlyph className="h-5 w-5 shrink-0" />
          {!collapsed && <span className="font-medium capitalize">{plan} Plan</span>}
        </Link>
      </div>
    </Sidebar>
  );
}
